import { useMemo } from 'react'

import {
  formatDate, matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'
import type { PublicationBookingModel, PublicationBookingRequestOptions } from '@models/types'
import { EntitiesState } from '@redux/entities'

import { matchBookingPreflightStatus, matchPublicationBookingStatus } from '@functions/publicationBooking'
import type { BookingStatusFilter, PreflightStatusFilter } from '@functions/publicationBooking'

const watchEntityKeys = ['publicationBookings']

type MatchFilterStringParams = {
  campaigns: EntitiesState['campaigns'],
  publicationBooking: PublicationBookingModel,
  publicationBookingItems: EntitiesState['publicationBookingItems'],
  filterString?: string,
}

const matchPublicationBookingString = (params: MatchFilterStringParams) => {
  const {
    campaigns, publicationBooking, publicationBookingItems, filterString,
  } = params
  const { booking_confirmation_id, id, publication_booking_item_ids = [] } = publicationBooking

  const matchId = id.toString() === filterString
  // @ts-expect-error TS2345
  const matchBookingConfirmationId = matchFilterString(booking_confirmation_id, filterString)

  const matchCampaignAddress = publication_booking_item_ids.some((publication_booking_item_id) => {
    const bookingItem = publicationBookingItems[publication_booking_item_id] || {}
    // @ts-expect-error TS2538
    const campaign = campaigns[bookingItem.campaign_id] || {}

    return matchFilterString(campaign.title, filterString)
  })

  return matchBookingConfirmationId || matchCampaignAddress || matchId
}

export type PublicationBookingFilters = {
  approval_date_start?: string,
  booking_status?: BookingStatusFilter,
  brand_id?: number,
  order_by?: string,
  preflight_status?: PreflightStatusFilter,
  publication_edition_id?: number,
  publication_id?: number,
  string?: string,
}

type UsePublicationBookingsOptions = {
  filters?: PublicationBookingFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationBookingRequestOptions,
}

function usePublicationBookings(options: UsePublicationBookingsOptions) {
  const { filters, requestOptions } = options
  const {
    approval_date_start: filterApprovalDateStart,
    booking_status: filterBookingStatus,
    brand_id: filterBrandId,
    order_by: orderBy = 'publication_bookings.id desc',
    preflight_status: filterPreflightStatus,
    publication_edition_id: filterPublicationEditionId,
    publication_id: filterPublicationId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { publicationBookings: publicationBookingsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    brandGroups, campaigns, publicationBookings, publicationBookingItems, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const filteredPublicationBookings = useMemo(() => {
    const filtered = Object.values(publicationBookings).filter((publicationBooking: PublicationBookingModel) => {
      const {
        brand_group_id, dates, publication_edition_id, publication_id,
      } = publicationBooking

      // @ts-expect-error TS2538
      const brandGroup = brandGroups[brand_group_id] || {}
      const approvedAt = formatDate(dates?.approval?.date_time, 'dd/MM/yyyy HH:mm')

      const matchApprovalDate = matchFilterBetweenDates(approvedAt, filterApprovalDateStart, null)
      const matchBookingStatus = matchPublicationBookingStatus(publicationBooking, statuses, filterBookingStatus)
      const matchBrand = matchFilterNumber(brandGroup.brand_id, filterBrandId)
      const matchPreflightStatus = matchBookingPreflightStatus(publicationBooking, statuses, filterPreflightStatus)
      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationEdition = matchFilterNumber(publication_edition_id, filterPublicationEditionId)

      const matchString = matchPublicationBookingString({
        campaigns, publicationBooking, publicationBookingItems, filterString,
      })

      return matchApprovalDate
        && matchBookingStatus
        && matchBrand
        && matchPreflightStatus
        && matchPublication
        && matchPublicationEdition
        && matchString
    })

    const [_, sortOrder] = orderBy.split('.')
    const [column, direction] = sortOrder.split(' ')

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, direction, column)
  }, [publicationBookingsUpdatedAt, JSON.stringify(filters), Object.values(statuses).length])

  const filteredPublicationBookingsCount = filteredPublicationBookings.length
  const hasFilteredPublicationBookings = !!filteredPublicationBookingsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationBookingsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationBookings } = useReduxAction(
    'publicationBookings',
    'loadPublicationBookings',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationBookings,
    filteredPublicationBookingsCount,
    hasFilteredPublicationBookings,
    loading: loadingPublicationBookings,
  }
}

export default usePublicationBookings
